/**
 * Splits model.json `paramMap` entries into watched form fields vs static API params.
 *
 * Field references: `{ "planId": "planId" }` → watch RHF field `planId`
 * Static constants: `{ "documentType": "$const:1" }` → always pass `documentType: 1`
 */
export const PARAM_MAP_CONST_PREFIX = '$const:';

export function isParamMapConst(value: string): boolean {
  return value.startsWith(PARAM_MAP_CONST_PREFIX);
}

export function parseParamMapConstValue(raw: string): string | number | boolean {
  if (/^\d+(\.\d+)?$/.test(raw)) return Number(raw);
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return raw;
}

export interface ResolvedParamMap {
  dataSourceParams: Record<string, string>;
  staticParams: Record<string, string | number | boolean>;
}

export function resolveParamMap(
  paramMap: Record<string, string> | undefined,
  namePrefix?: string,
): ResolvedParamMap {
  const dataSourceParams: Record<string, string> = {};
  const staticParams: Record<string, string | number | boolean> = {};

  if (!paramMap) {
    return { dataSourceParams, staticParams };
  }

  for (const [apiParam, fieldPathOrConst] of Object.entries(paramMap)) {
    if (isParamMapConst(fieldPathOrConst)) {
      staticParams[apiParam] = parseParamMapConstValue(
        fieldPathOrConst.slice(PARAM_MAP_CONST_PREFIX.length),
      );
    } else {
      dataSourceParams[apiParam] = namePrefix
        ? `${namePrefix}.${fieldPathOrConst}`
        : fieldPathOrConst;
    }
  }

  return { dataSourceParams, staticParams };
}

export function extractStaticParamsFromParamMap(
  paramMap: Record<string, string> | undefined,
): Record<string, string | number | boolean> | undefined {
  const { staticParams } = resolveParamMap(paramMap);
  return Object.keys(staticParams).length > 0 ? staticParams : undefined;
}
